/**
 * Coup de pouce — Attendu 1051 « reconnaitre des fractions égales :
 * 2/3 = …/15 ; 4/7 = …/14 ». UNE page STATIQUE partagée par les cinq
 * generator_keys (un seul geste). Écrit à l'étape 5 de revue-gen, contre la
 * doctrine (skill doctrine-coup-de-pouce), validé par le prof le 2026-09-08.
 *
 * L'image que l'élève a déjà : la bande de papier graduée du CM1 (« 3
 * graduations pour la fraction 3/4 », programme cycle 3), les deux axes
 * alignés de 6e (gen_627 « Axes gradués : points alignés »), les arcs
 * comptés de 1050 et la carte « Le même point » de 1051. Cinq éléments,
 * deux couleurs, un seul mot pour chaque objet (parts, unité) :
 *   1. une droite de 0 à 1 en quarts, A à 3/4, trois arcs orange comptés
 *      depuis 0, « 1 unité = 4 parts » en bleu dessous ;
 *   2. juste dessous, la même droite, chaque part partagée en 2 par un petit
 *      trait bleu, A à la même verticale (pointillé), six arcs comptés,
 *      « 1 unité = 8 parts » ;
 *   3. en gros : 3/4 = 6/8, « × 2 » en orange du 3 vers le 6, « × 2 » en
 *      bleu du 4 vers le 8, chaque chiffre relié à son comptage par la
 *      couleur ;
 *   4. la règle, cause puis effet : « Je partage chaque part en 2 : il y a
 *      2 fois plus de parts, en bas comme en haut. » (« partager », le mot du
 *      programme depuis le CM1) ; la vérification est portée par les deux
 *      « × 2 » de la figure ;
 *   5. ce qu'il faut retenir : « Même point, même nombre. »
 * Un seul exemple, × 2, alors que les cartes vont jusqu'à × 10 : le
 * transfert « partagée en 5 » est le même geste, une fiche à deux exemples
 * ne se lit pas. Interdits : coefficient, agrandir, simplifier, toute lettre.
 *
 * TRIPLET SACRIFIÉ : (3, 4, 2), 3/4 = 6/8 ne sort d'aucune carte (gen_1051,
 * auto-testé par cdp1051.self.test.js). Largeur bornée à 340 px, tailles
 * de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';

/** Les droites : 0 → 1 sur 320 px. */
const X0 = 40;
const UNITE = 320;
const Y1 = 70;
const Y2 = 152;
const XA = X0 + (3 / 4) * UNITE;
const toX = (v) => X0 + v * UNITE;

/** Une droite de 0 à 1 graduée en `den` parts ; les traits nouveaux (impairs en huitièmes) en bleu. */
const droite = (y, den, nouveauxEnBleu) => {
    const traits = Array.from({ length: den + 1 }, (_, k) => {
        const x = toX(k / den);
        const entier = k === 0 || k === den;
        const nouveau = nouveauxEnBleu && k % 2 === 1;
        const h = entier ? 9 : 5;
        return `<line x1="${x}" y1="${y - h}" x2="${x}" y2="${y + h}" stroke="${nouveau ? BLEU : ENCRE}" stroke-width="${entier ? 2 : nouveau ? 1.6 : 1.2}"/>`
            + (entier ? `<text x="${x}" y="${y + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${k / den}</text>` : '');
    }).join('\n  ');
    return `<line x1="${X0 - 10}" y1="${y}" x2="${toX(1) + 24}" y2="${y}" stroke="${ENCRE}" stroke-width="2"/>
  <polygon points="${toX(1) + 32},${y} ${toX(1) + 22},${y - 5} ${toX(1) + 22},${y + 5}" fill="${ENCRE}"/>
  ${traits}
  <circle cx="${XA}" cy="${y}" r="5" fill="${ORANGE}"/>
  <text x="${XA}" y="${y + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ORANGE}">A</text>`;
};

/** Les arcs orange de 0 à A, un par part, numérotés. */
const arcs = (y, den) => Array.from({ length: (3 * den) / 4 }, (_, k) => {
    const x1 = toX(k / den);
    const x2 = toX((k + 1) / den);
    const xm = (x1 + x2) / 2;
    return `<path d="M ${x1} ${y - 10} Q ${xm} ${y - 30} ${x2} ${y - 10}" fill="none" stroke="${ORANGE}" stroke-width="1.6"/>
  <text x="${xm}" y="${y - 27}" font-size="9" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${k + 1}</text>`;
}).join('\n  ');

/** Le trait bleu « 1 unité = n parts » sous une droite. */
const unite = (y, n) => `<line x1="${toX(0)}" y1="${y + 30}" x2="${toX(1)}" y2="${y + 30}" stroke="${BLEU}" stroke-width="1.6"/>
  <line x1="${toX(0)}" y1="${y + 26}" x2="${toX(0)}" y2="${y + 34}" stroke="${BLEU}" stroke-width="1.6"/>
  <line x1="${toX(1)}" y1="${y + 26}" x2="${toX(1)}" y2="${y + 34}" stroke="${BLEU}" stroke-width="1.6"/>
  <text x="${toX(0.5)}" y="${y + 46}" font-size="11" font-weight="bold" text-anchor="middle" fill="${BLEU}">1 unité = ${n} parts</text>`;

/** Une fraction empilée, numérateur orange, dénominateur bleu. */
const frac = (x, y, n, d) => `<text x="${x}" y="${y - 5}" font-size="17" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${n}</text>
  <line x1="${x - 11}" y1="${y}" x2="${x + 11}" y2="${y}" stroke="${ENCRE}" stroke-width="1.8"/>
  <text x="${x}" y="${y + 18}" font-size="17" font-weight="bold" text-anchor="middle" fill="${BLEU}">${d}</text>`;

const YF = 246;
const SVG_MEME_POINT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 356" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="Une droite de 0 à 1 partagée en 4 parts, un point A : trois parts depuis 0, 1 unité égale 4 parts. Dessous, la même droite avec chaque part partagée en 2, le même point A : six parts depuis 0, 1 unité égale 8 parts. 3 sur 4 égale 6 sur 8 : le 3 est multiplié par 2, le 4 aussi. Je partage chaque part en 2 : il y a 2 fois plus de parts, en bas comme en haut. Même point, même nombre.">
  <!-- 1. la droite en quarts, A à 3/4 -->
  ${arcs(Y1, 4)}
  ${droite(Y1, 4, false)}
  ${unite(Y1, 4)}

  <!-- 2. la même droite, chaque part partagée en 2 -->
  <line x1="${XA}" y1="${Y1 + 6}" x2="${XA}" y2="${Y2 - 32}" stroke="${ORANGE}" stroke-width="1.2" stroke-dasharray="3 3"/>
  ${arcs(Y2, 8)}
  ${droite(Y2, 8, true)}
  ${unite(Y2, 8)}

  <!-- 3. la fraction, × 2 en haut en orange, × 2 en bas en bleu -->
  ${frac(160, YF, 3, 4)}
  <text x="200" y="${YF + 6}" font-size="17" font-weight="bold" text-anchor="middle" fill="${ENCRE}">=</text>
  ${frac(240, YF, 6, 8)}
  <path d="M 168 ${YF - 20} Q 200 ${YF - 36} 232 ${YF - 20}" fill="none" stroke="${ORANGE}" stroke-width="1.6"/>
  <text x="200" y="${YF - 31}" font-size="11" font-weight="bold" text-anchor="middle" fill="${ORANGE}">× 2</text>
  <path d="M 168 ${YF + 24} Q 200 ${YF + 40} 232 ${YF + 24}" fill="none" stroke="${BLEU}" stroke-width="1.6"/>
  <text x="200" y="${YF + 44}" font-size="11" font-weight="bold" text-anchor="middle" fill="${BLEU}">× 2</text>

  <!-- 4. la règle, cause puis effet (revue prof 2026-09-08 : « trop d'infos »,
       la version « 4 parts deviennent 8, 3 parts deviennent 6 » donnait le
       résultat sans la cause ; la vérification est portée par les deux « × 2 ») -->
  <text x="200" y="302" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">Je partage chaque part en 2 :</text>
  <text x="200" y="320" font-size="12" text-anchor="middle" fill="${ENCRE}">il y a 2 fois plus de parts, en <tspan fill="${BLEU}">bas</tspan> comme en <tspan fill="${ORANGE}">haut</tspan>.</text>

  <!-- 5. ce qu'il faut retenir -->
  <text x="200" y="344" font-size="12.5" font-weight="bold" text-anchor="middle" fill="${ENCRE}">Même point, même nombre.</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: 'Le même point, chaque part partagée en 2', svg: SVG_MEME_POINT },
    ],
};

const CP_1051 = {
    flash_texte_trou_saisie: AIDE,
    meme_qcm_unique: AIDE,
    meme_tableau_vf: AIDE,
    meme_relier: AIDE,
    point_texte_trou_saisie: AIDE,
};

export default CP_1051;
